import type { HsMeasurementPoint } from './measurement.function';

/** A ray from the camera through a screen point, in world coordinates; `direction` has unit length. */
export interface HsScreenRay {
  origin: HsMeasurementPoint;
  direction: HsMeasurementPoint;
}

/** What `hsScreenRay` reads of a perspective camera. */
export interface HsScreenRayCamera {
  readonly worldMatrix: { readonly values: ArrayLike<number> };
  readonly projectionMatrixInverse: { readonly values: ArrayLike<number> };
}

/** The canvas box in CSS pixels, as `getBoundingClientRect` gives it. */
export interface HsScreenRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * The ray through a client (CSS pixel) point of a canvas. The canvas box must be in CSS pixels as well:
 * mixing in drawing-buffer pixels (device pixel ratio, downsampling) bends the ray off the cursor.
 */
export function hsScreenRay(
  camera: HsScreenRayCamera,
  rect: HsScreenRect,
  clientX: number,
  clientY: number
): HsScreenRay | null {
  if (rect.width <= 0 || rect.height <= 0) {
    return null;
  }
  const ndcX: number = ((clientX - rect.left) / rect.width) * 2 - 1;
  const ndcY: number = 1 - ((clientY - rect.top) / rect.height) * 2;
  const world: ArrayLike<number> = camera.worldMatrix.values;
  const origin: HsMeasurementPoint = [world[12], world[13], world[14]];
  // Unproject a point between the near and far planes: projection inverse to camera space, then to world.
  const inverse: ArrayLike<number> = camera.projectionMatrixInverse.values;
  const view: number[] = transform(inverse, [ndcX, ndcY, 0.5, 1]);
  const camera3: number[] = [view[0] / view[3], view[1] / view[3], view[2] / view[3], 1];
  const target: number[] = transform(world, camera3);
  const direction: number[] = [target[0] - origin[0], target[1] - origin[1], target[2] - origin[2]];
  const length: number = Math.hypot(direction[0], direction[1], direction[2]);
  if (length === 0) {
    return null;
  }
  return { origin, direction: [direction[0] / length, direction[1] / length, direction[2] / length] };
}

/** What `hsProjectToScreen` reads of a camera. */
export interface HsScreenProjectionCamera {
  readonly worldMatrixInverse: { readonly values: ArrayLike<number> };
  readonly projectionMatrix: { readonly values: ArrayLike<number> };
}

/**
 * The client (CSS pixel) point a world point shows at in a canvas box, for labels drawn over the 3D view; null
 * behind the camera.
 */
export function hsProjectToScreen(
  camera: HsScreenProjectionCamera,
  rect: HsScreenRect,
  point: HsMeasurementPoint
): { x: number; y: number } | null {
  const view: number[] = transform(camera.worldMatrixInverse.values, [point[0], point[1], point[2], 1]);
  const clip: number[] = transform(camera.projectionMatrix.values, view);
  if (clip[3] <= 0) {
    return null;
  }
  return {
    x: rect.left + ((clip[0] / clip[3] + 1) / 2) * rect.width,
    y: rect.top + ((1 - clip[1] / clip[3]) / 2) * rect.height,
  };
}

/** Where the ray meets the horizontal plane at `height` in front of the camera, or null. */
export function hsIntersectRayWithLevel(ray: HsScreenRay, height: number): HsMeasurementPoint | null {
  const directionY: number = ray.direction[1];
  if (Math.abs(directionY) < 0.000001) {
    return null;
  }
  const distance: number = (height - ray.origin[1]) / directionY;
  if (!Number.isFinite(distance) || distance <= 0) {
    return null;
  }
  return [
    ray.origin[0] + ray.direction[0] * distance,
    ray.origin[1] + ray.direction[1] * distance,
    ray.origin[2] + ray.direction[2] * distance,
  ];
}

/** Column-major 4x4 matrix times a 4-vector. */
function transform(matrix: ArrayLike<number>, vector: readonly number[]): number[] {
  const result: number[] = [0, 0, 0, 0];
  for (let row: number = 0; row < 4; row++) {
    result[row] =
      matrix[row] * vector[0] +
      matrix[4 + row] * vector[1] +
      matrix[8 + row] * vector[2] +
      matrix[12 + row] * vector[3];
  }
  return result;
}
